import { useState } from "react";
import { ChevronDown, ChevronRight, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import CodeBlock from "./CodeBlock";

function SequenceDiagram({ steps }) {
  return (
    <div className="my-6 p-4 rounded-lg border border-zinc-800 bg-zinc-950/50 font-mono text-xs">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-4">
        Sequence Diagram
      </div>
      <div className="space-y-3">
        {steps.map((step, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-24 text-right text-zinc-500 shrink-0">{step.from}</div>
            <div className="flex-1 flex items-center gap-2">
              <div className="h-px border-t border-dashed border-zinc-700 flex-1" />
              <div className="px-2 py-1 rounded bg-zinc-900 border border-zinc-800 text-zinc-300 text-[11px] whitespace-nowrap">
                {step.label}
              </div>
              <ArrowRight size={14} className="text-amber-500 shrink-0" />
            </div>
            <div className="w-24 text-zinc-500 shrink-0">{step.to}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GuideSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-zinc-900/50 hover:bg-zinc-900 transition-colors text-left"
      >
        {open ? <ChevronDown size={16} className="text-amber-500" /> : <ChevronRight size={16} className="text-zinc-500" />}
        <span className="font-mono text-sm font-bold text-zinc-200">{title}</span>
      </button>
      {open && <div className="p-4 space-y-4 border-t border-zinc-800">{children}</div>}
    </div>
  );
}

const FIRST_API_CALL_CODE = [
  "// JavaScript example",
  "const response = await fetch(",
  '  "https://api.maxoscloud.com/v1/agents?limit=10",',
  '  { headers: { "X-API-Key": "sk_live_your_key_here" } }',
  ");",
  "const { data } = await response.json();",
  'console.log("Found " + data.length + " agents");',
].join("\n");

const WEBHOOK_ENDPOINT_CODE = [
  "# Example Express.js endpoint",
  'app.post("/webhook", (req, res) => {',
  '  const signature = req.headers["x-maxos-signature"];',
  "  // Verify signature",
  "  const event = req.body;",
  '  console.log("Received:", event.event);',
  '  res.status(200).send("OK");',
  "});",
].join("\n");

const WEBHOOK_CURL_CODE = [
  'curl -X POST "https://api.maxoscloud.com/v1/webhooks" \\',
  '  -H "X-API-Key: sk_live_your_key_here" \\',
  '  -H "Content-Type: application/json" \\',
  "  -d '{",
  '    "url": "https://your-server.com/webhook",',
  '    "events": ["message.received", "agent.created"],',
  '    "secret": "your_signing_secret"',
  "  }'",
].join("\n");

const VERIFY_SIGNATURE_CODE = [
  "// Verify webhook signature",
  'const crypto = require("crypto");',
  "",
  "function verifySignature(payload, signature, secret) {",
  "  const expected = crypto",
  '    .createHmac("sha256", secret)',
  "    .update(payload)",
  '    .digest("hex");',
  "  return crypto.timingSafeEqual(",
  "    Buffer.from(signature),",
  "    Buffer.from(expected)",
  "  );",
  "}",
].join("\n");

const OAUTH_CREDENTIALS_CODE = [
  "# After registration you receive:",
  'CLIENT_ID = "cli_abc123"',
  'CLIENT_SECRET = "cs_xyz789secret"',
  'REDIRECT_URI = "https://your-app.com/callback"',
].join("\n");

const OAUTH_AUTHORIZE_CODE = [
  "https://auth.maxoscloud.com/oauth/authorize",
  "  ?client_id=cli_abc123",
  "  &redirect_uri=https://your-app.com/callback",
  "  &response_type=code",
  "  &scope=agents:read agents:write messages:read",
  "  &state=random_state_string",
].join("\n");

const OAUTH_TOKEN_CODE = [
  'curl -X POST "https://auth.maxoscloud.com/oauth/token" \\',
  '  -H "Content-Type: application/json" \\',
  "  -d '{",
  '    "grant_type": "authorization_code",',
  '    "code": "auth_code_from_callback",',
  '    "client_id": "cli_abc123",',
  '    "client_secret": "cs_xyz789secret",',
  '    "redirect_uri": "https://your-app.com/callback"',
  "  }'",
  "",
  "# Response:",
  "# {",
  '#   "access_token": "at_abc123...",',
  '#   "token_type": "Bearer",',
  '#   "expires_in": 3600,',
  '#   "refresh_token": "rt_xyz789..."',
  "# }",
].join("\n");

const OAUTH_USAGE_CODE = [
  "const response = await fetch(",
  '  "https://api.maxoscloud.com/v1/agents",',
  "  {",
  "    headers: {",
  '      "Authorization": "Bearer at_abc123..."',
  "    }",
  "  }",
  ");",
].join("\n");

export default function IntegrationGuide() {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <p className="text-sm text-zinc-400">{t("api_docs.guides.subtitle")}</p>

      {/* Guide 1: Your First API Call */}
      <GuideSection title={t("api_docs.guides.first_api_call.title")} defaultOpen={true}>
        <p className="text-sm text-zinc-400">{t("api_docs.guides.first_api_call.description")}</p>

        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-mono text-xs font-bold">1</span>
              <h4 className="font-mono text-sm font-bold text-zinc-200">{t("api_docs.guides.first_api_call.step_1_title")}</h4>
            </div>
            <p className="text-xs text-zinc-500 ml-8">{t("api_docs.guides.first_api_call.step_1_desc")}</p>
            <div className="ml-8">
              <CodeBlock
                code="# Your API key will look like this:\nsk_live_abc123xyz789..."
                language="bash"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-mono text-xs font-bold">2</span>
              <h4 className="font-mono text-sm font-bold text-zinc-200">{t("api_docs.guides.first_api_call.step_2_title")}</h4>
            </div>
            <p className="text-xs text-zinc-500 ml-8">{t("api_docs.guides.first_api_call.step_2_desc")}</p>
            <div className="ml-8">
              <CodeBlock
                code={'curl -X GET "https://api.maxoscloud.com/v1/agents?limit=10" \\\n  -H "X-API-Key: sk_live_your_key_here"'}
                language="bash"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-mono text-xs font-bold">3</span>
              <h4 className="font-mono text-sm font-bold text-zinc-200">{t("api_docs.guides.first_api_call.step_3_title")}</h4>
            </div>
            <p className="text-xs text-zinc-500 ml-8">{t("api_docs.guides.first_api_call.step_3_desc")}</p>
            <div className="ml-8">
              <CodeBlock code={FIRST_API_CALL_CODE} language="javascript" />
            </div>
          </div>

          <SequenceDiagram
            steps={[
              { from: "Your App", to: "Max OS API", label: "GET /v1/agents" },
              { from: "Max OS API", to: "Your App", label: "200 OK + JSON" },
            ]}
          />
        </div>
      </GuideSection>

      {/* Guide 2: Webhook Setup */}
      <GuideSection title={t("api_docs.guides.webhook_setup.title")}>
        <p className="text-sm text-zinc-400">{t("api_docs.guides.webhook_setup.description")}</p>

        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-mono text-xs font-bold">1</span>
              <h4 className="font-mono text-sm font-bold text-zinc-200">{t("api_docs.guides.webhook_setup.step_1_title")}</h4>
            </div>
            <p className="text-xs text-zinc-500 ml-8">{t("api_docs.guides.webhook_setup.step_1_desc")}</p>
            <div className="ml-8">
              <CodeBlock code={WEBHOOK_ENDPOINT_CODE} language="javascript" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-mono text-xs font-bold">2</span>
              <h4 className="font-mono text-sm font-bold text-zinc-200">{t("api_docs.guides.webhook_setup.step_2_title")}</h4>
            </div>
            <p className="text-xs text-zinc-500 ml-8">{t("api_docs.guides.webhook_setup.step_2_desc")}</p>
            <div className="ml-8">
              <CodeBlock code={WEBHOOK_CURL_CODE} language="bash" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-mono text-xs font-bold">3</span>
              <h4 className="font-mono text-sm font-bold text-zinc-200">{t("api_docs.guides.webhook_setup.step_3_title")}</h4>
            </div>
            <p className="text-xs text-zinc-500 ml-8">{t("api_docs.guides.webhook_setup.step_3_desc")}</p>
            <div className="ml-8">
              <CodeBlock code={VERIFY_SIGNATURE_CODE} language="javascript" />
            </div>
          </div>

          <SequenceDiagram
            steps={[
              { from: "Max OS", to: "Your Server", label: "POST /webhook" },
              { from: "Your Server", to: "Max OS", label: "200 OK" },
              { from: "Your Server", to: "Database", label: "Store event" },
            ]}
          />
        </div>
      </GuideSection>

      {/* Guide 3: OAuth Flow */}
      <GuideSection title={t("api_docs.guides.oauth_flow.title")}>
        <p className="text-sm text-zinc-400">{t("api_docs.guides.oauth_flow.description")}</p>

        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-mono text-xs font-bold">1</span>
              <h4 className="font-mono text-sm font-bold text-zinc-200">{t("api_docs.guides.oauth_flow.step_1_title")}</h4>
            </div>
            <p className="text-xs text-zinc-500 ml-8">{t("api_docs.guides.oauth_flow.step_1_desc")}</p>
            <div className="ml-8">
              <CodeBlock code={OAUTH_CREDENTIALS_CODE} language="bash" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-mono text-xs font-bold">2</span>
              <h4 className="font-mono text-sm font-bold text-zinc-200">{t("api_docs.guides.oauth_flow.step_2_title")}</h4>
            </div>
            <p className="text-xs text-zinc-500 ml-8">{t("api_docs.guides.oauth_flow.step_2_desc")}</p>
            <div className="ml-8">
              <CodeBlock code={OAUTH_AUTHORIZE_CODE} language="bash" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-mono text-xs font-bold">3</span>
              <h4 className="font-mono text-sm font-bold text-zinc-200">{t("api_docs.guides.oauth_flow.step_3_title")}</h4>
            </div>
            <p className="text-xs text-zinc-500 ml-8">{t("api_docs.guides.oauth_flow.step_3_desc")}</p>
            <div className="ml-8">
              <CodeBlock code={OAUTH_TOKEN_CODE} language="bash" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 font-mono text-xs font-bold">4</span>
              <h4 className="font-mono text-sm font-bold text-zinc-200">{t("api_docs.guides.oauth_flow.step_4_title")}</h4>
            </div>
            <p className="text-xs text-zinc-500 ml-8">{t("api_docs.guides.oauth_flow.step_4_desc")}</p>
            <div className="ml-8">
              <CodeBlock code={OAUTH_USAGE_CODE} language="javascript" />
            </div>
          </div>

          <SequenceDiagram
            steps={[
              { from: "User", to: "Your App", label: "Click Connect" },
              { from: "Your App", to: "Max OS Auth", label: "OAuth authorize" },
              { from: "Max OS Auth", to: "User", label: "Consent screen" },
              { from: "User", to: "Your App", label: "Redirect + code" },
              { from: "Your App", to: "Max OS Auth", label: "Exchange code" },
              { from: "Max OS Auth", to: "Your App", label: "Access token" },
            ]}
          />
        </div>
      </GuideSection>
    </div>
  );
}
